import {
  AUTOPAY_CONFIRMATION_ROUTE,
  AUTOPAY_ENROLL_ROUTE,
  AUTOPAY_UNENROLL_ROUTE,
  BILLING_HISTORY_ROUTE,
  BILLING_ROUTE,
  BRIGHTBOX_RETRO_INTEREST,
  CASES_ROUTE,
  CUSTOMER_APP,
  DASHBOARD_HISTORY_ROUTE,
  DASHBOARD_ROUTE,
  DOCUMENT_LIST_ROUTE,
  FEEDBACK_ROUTE,
  LG_BATTERY_ISSUE_ROUTE,
  LOGIN_EMAIL_ROUTE,
  LOGIN_HELP_ROUTE,
  LOGIN_PHONE_ROUTE,
  LOGIN_ROUTE,
  LOGIN_TOS_ROUTE,
  PAYMENT_CONFIRMATION_ROUTE,
  PAYMENT_OPTIONS_ROUTE,
  PROFILE_ROUTE,
  REFERRALS_ROUTE,
  TERMS_OF_SERVICE_ROUTE,
  SOLAR_GUIDE_ROUTE,
  SUPPORT_ROUTE,
} from 'constants/routes';
import React, { useEffect, useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { FullScreenLoading } from 'components/loading';
import SecureRoute from 'components/secureRoute';
import ConditionalRoute from 'components/conditionalRoute';
import FullScreenError from 'components/error/FullScreenError';
import ErrorBoundary from 'components/errorBoundary';
import { PerformanceProvider } from '@sunrun/recent-performance-charts';
import { Provider, useDispatch } from 'react-redux';
import axios from 'axios';
import styled from '@emotion/styled';
import config from 'config';
import withSizes from 'react-sizes';
import { useClearCache } from '../hooks/useClearCache';
import rollbar, { logApiError } from '../utilities/rollbar';
import * as analytics from '../utilities/analytics';
import { setAuthToken, getAuthToken } from '../providers/authTokenProvider';
import store from '../redux';
import { changeDefaultProspectId } from '../redux/modules/app';
import MaterialUITheme from '../globalMaterialUITheme';
import GlobalStyles from '../globalStyles';
import { useIsLoggedIn, useIsLoggingOut } from '../hooks/useAuthentication';
import { useSideDrawerOpen } from '../hooks/useApp';
import {
  useBillingEnabled,
  useCurrentOwner,
  useIsOrangePostPTO,
  useIsBlueBillingSystem,
  useMonthlyType,
  useRelatedContact,
  useIsEVChargerProspect,
} from '../hooks/useUser';
import Navigation from '../components/navigation';
import SystemNotification from '../components/general/SystemNotification';
import VersionDetect from '../components/versioning/VersionDetect';
import Header from '../layouts/Header';
import { MIN_DESKTOP_BREAKPOINT } from '../components/general/constants/_breakpoints';
import '../i18n';
import {
  ONE_TIME_BILLING_ROUTE,
  SPECIAL_INVOICE_PAYMENT_ROUTE,
  FAQ_ROUTE,
  BATTERY_RETRO_INTEREST,
  METER_REPLACEMENT_INFO_ROUTE,
  RTU_METER_REPLACEMENT_INFO_ROUTE,
} from '../constants/routes';
import mapSizesToProps from '../components/general/utilities/mapSizesToProps';
import { isReactNativeApp, postReactNativeMessage } from '../utilities/mobileRNApp';
import { HEADER_HEIGHT } from '../components/general/constants/_sizes';
import SunsetModal from '../components/SunsetModal/SunsetModal';
import { useLegacyAppSunsetModalFlag } from '../hooks/flagsmith/useLegacyAppSunsetModalFlag';
import CookieLoginContainer from './CookieLoginContainer';
import FlagsmithContainer from './FlagsmithContainer';

// eslint-disable-next-line no-underscore-dangle
const sharedData = window.__sharedData || {};

const AutopayEnroll = lazy(() => import('../components/billing/experiencePayments/AutopayEnroll.container'));
const AutopayManage = lazy(() => import('../components/billing/AutopayManage.container'));
const AutopayUnenroll = lazy(() => import('../components/billing/AutopayUnenrollConfirmation'));
const BatteryRetroComponent = lazy(() => import('../components/battery/RetrofitInterest'));
const Billing = lazy(() => import('../components/billing/Billing.container'));
const BillingConfirmation = lazy(() => import('../components/billing/experiencePayments/BillingConfirmation.container'));
const BillingHistory = lazy(() => import('../components/billing/BillingHistory.container'));
const Cases = lazy(() => import('../components/cases/Cases.container'));
const CustomerAppPage = lazy(() => import('../pages/CustomerApp'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Documents = lazy(() => import('../components/documents/Documents.container'));
const TermsOfService = lazy(() => import('../components/accountDetails/TermsOfService'));
const FAQPage = lazy(() => import('../components/faq/FAQ.container'));
const Feedback = lazy(() => import('../components/feedback/Feedback.container'));
const LGBatteryInfo = lazy(() => import('../pages/LGSafety'));
const LoginPage = lazy(() => import('../pages/Login'));
const MeterReplacementInfo = lazy(() => import('../components/meterReplacement/MeterReplacementInformationPage'));
const OneTimePayment = lazy(() => import('../components/billing/experiencePayments/OneTimePayment.container'));

const Profile = lazy(() => import('../pages/Account'));
const Referrals = lazy(() => import('../components/referrals/Referrals.container'));
const SolarGuide = lazy(() => import('../components/solarGuide/SolarGuide.container'));
const Support = lazy(() => import('../components/support/Support.container'));
const SystemHistory = lazy(() => import('../pages/SystemHistory'));
const TrackerEV = lazy(() => import('../components/trackerEV/TrackerEV.container'));
const UnknownPage = lazy(() => import('../pages/Unknown'));

axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.interceptors.request.use(axiosConfig => {
  if (getAuthToken()) {
    // eslint-disable-next-line no-param-reassign
    axiosConfig.headers.Authorization = getAuthToken();
  }

  return axiosConfig;
});
axios.interceptors.response.use(
  res => res,
  err => {
    // Send all API errors to rollbar
    try {
      logApiError(err);
    } catch (loggingError) {
      window.Rollbar && window.Rollbar.error('Error logging error', err);
    }

    throw err;
  },
);

const StyledApp = styled.div`
  height: 100%;
  @media only screen and (min-width: ${MIN_DESKTOP_BREAKPOINT}) {
    display: flex;
    height: ${props => (props.loggedIn ? 'auto' : '100%')};
    min-height: calc(100% - ${HEADER_HEIGHT});

    > div:nth-of-type(1) {
      width: 250px;
    }

    > div:last-of-type {
      width: calc(100% - 250px);
    }

    > div:only-child {
      width: 100%;
    }
  }
`;

const Routes = ({
  allowCybersourcePaymentsConditions,
  billingAndMonthlyTypeConditions,
  currentOwnerConditions,
  isCurrentOwner,
  location,
  isRelatedContact,
}) => {
  const [dashboardComponent, setDashboardComponent] = useState(Billing);
  const isEVChargerProspect = useIsEVChargerProspect();

  useEffect(() => {
    analytics.gtag('config', config.GOOGLE_ANALYTICS_TRACKING_ID, {
      page_title: location.pathname,
      page_path: location.pathname,
    });
  }, [location]);

  useEffect(() => {
    const defaultDashboard = isCurrentOwner ? Dashboard : Billing;
    setDashboardComponent(isEVChargerProspect ? TrackerEV : defaultDashboard);
  }, [isEVChargerProspect, isCurrentOwner]);

  return (
    <Switch>
      <Route path={LOGIN_ROUTE} component={LoginPage} />
      <Route path={LOGIN_PHONE_ROUTE} component={LoginPage} />
      <Route path={LOGIN_EMAIL_ROUTE} component={LoginPage} />
      <Route path={LOGIN_HELP_ROUTE} component={LoginPage} />
      <Route path={LOGIN_TOS_ROUTE} component={LoginPage} />
      <Route path={CUSTOMER_APP} component={CustomerAppPage} />
      <SecureRoute path={`${PROFILE_ROUTE}/${TERMS_OF_SERVICE_ROUTE}`} component={TermsOfService} />
      <SecureRoute path={PROFILE_ROUTE} component={Profile} />
      <SecureRoute path={DOCUMENT_LIST_ROUTE} component={Documents} />
      <SecureRoute path={SUPPORT_ROUTE} component={Support} />
      <SecureRoute path={FEEDBACK_ROUTE} component={Feedback} />
      {config.ENABLE_CASE_TABLE && <SecureRoute path={CASES_ROUTE} component={Cases} />}
      <SecureRoute exact path={DASHBOARD_ROUTE} component={dashboardComponent} />
      <ConditionalRoute conditions={currentOwnerConditions} exact path={DASHBOARD_HISTORY_ROUTE} component={SystemHistory} />
      <SecureRoute path={METER_REPLACEMENT_INFO_ROUTE} component={MeterReplacementInfo} />
      <SecureRoute path={RTU_METER_REPLACEMENT_INFO_ROUTE} component={MeterReplacementInfo} />
      <SecureRoute path={FAQ_ROUTE} component={FAQPage} />
      <SecureRoute path={SOLAR_GUIDE_ROUTE} component={SolarGuide} />
      <SecureRoute path={REFERRALS_ROUTE} component={Referrals} />
      <SecureRoute path={BATTERY_RETRO_INTEREST} component={BatteryRetroComponent} />
      <SecureRoute path={BRIGHTBOX_RETRO_INTEREST} component={BatteryRetroComponent} />
      <ConditionalRoute conditions={{ notRelated: !isRelatedContact }} path={`${BILLING_ROUTE}${BILLING_HISTORY_ROUTE}`} component={BillingHistory} />
      {/* regular one time payment */}
      <ConditionalRoute conditions={allowCybersourcePaymentsConditions} path={`${BILLING_ROUTE}${ONE_TIME_BILLING_ROUTE}`} component={OneTimePayment} />

      {/* special invoice payment */}
      <SecureRoute path={`${BILLING_ROUTE}${SPECIAL_INVOICE_PAYMENT_ROUTE}`} component={OneTimePayment} />

      {/* payment confirmation */}
      <SecureRoute path={`${BILLING_ROUTE}${PAYMENT_CONFIRMATION_ROUTE}`} component={BillingConfirmation} />

      {/* autopay unenrollment */}
      <ConditionalRoute
        conditions={billingAndMonthlyTypeConditions}
        path={`${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}/${AUTOPAY_UNENROLL_ROUTE}`}
        component={AutopayUnenroll}
      />

      {/* autopay enrollment */}
      <ConditionalRoute
        conditions={billingAndMonthlyTypeConditions}
        path={`${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}/${AUTOPAY_ENROLL_ROUTE}`}
        component={AutopayEnroll}
      />

      {/* autopay confirmation */}
      <SecureRoute path={`${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}/${AUTOPAY_CONFIRMATION_ROUTE}`} component={BillingConfirmation} />

      {/* manage autopay */}
      <ConditionalRoute
        conditions={billingAndMonthlyTypeConditions}
        path={`${BILLING_ROUTE}${PAYMENT_OPTIONS_ROUTE}`}
        component={isCurrentOwner ? AutopayManage : Billing}
      />
      <SecureRoute path={BILLING_ROUTE} component={Billing} />
      <SecureRoute path={LG_BATTERY_ISSUE_ROUTE} component={LGBatteryInfo} />
      {/* Default, 404 page */}
      <SecureRoute component={UnknownPage} />
    </Switch>
  );
};

Routes.propTypes = {
  allowCybersourcePaymentsConditions: PropTypes.shape({}).isRequired,
  billingAndMonthlyTypeConditions: PropTypes.shape({}).isRequired,
  currentOwnerConditions: PropTypes.shape({}).isRequired,
  isCurrentOwner: PropTypes.bool,
  isRelatedContact: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
};
Routes.defaultProps = {
  isCurrentOwner: true,
  isRelatedContact: false,
};

const AppAndRoutes = ({
  allowCybersourcePaymentsConditions,
  billingAndMonthlyTypeConditions,
  currentOwnerConditions,
  isCurrentOwner,
  isRelatedContact,
  isLoggingOut,
  isMobile,
  loggedIn,
  sideDrawerOpen,
}) => {
  const { isLatestVersion, emptyCacheStorage } = useClearCache();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const legacyAppSunsetModalFlag = useLegacyAppSunsetModalFlag();
  const isReactNative = isReactNativeApp();
  const showSunsetModal = Boolean(legacyAppSunsetModalFlag && isReactNative);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('token')) {
      setAuthToken(urlParams.token);
    }

    if (urlParams.get('pId')) {
      dispatch(changeDefaultProspectId(urlParams.get('pId')));
      urlParams.delete('pId');
      history.replace({
        search: urlParams.toString(),
      });
    }

    // setup global rollbar var
    window.Rollbar = rollbar;

    // include gtag scripts
    const script = document.createElement('script');

    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.GOOGLE_ANALYTICS_TRACKING_ID}`;
    script.async = false;

    document.body.appendChild(script);
    analytics.initialize();

    const touchStart = () => postReactNativeMessage(JSON.stringify('touchStart'));
    const touchEnd = () => postReactNativeMessage(JSON.stringify('touchEnd'));

    if (isReactNativeApp()) {
      window.addEventListener('touchstart', touchStart);
      window.addEventListener('touchend', touchEnd);
    }
    return () => {
      if (isReactNativeApp()) {
        window.removeEventListener('touchend', touchStart);
        window.removeEventListener('touchend', touchEnd);
      }
    };
  }, []);

  const CustomFullScreenLoading = () => (
    <>
      {/* this div handles first-of-type styling when not logged in and the nav isn't there */}
      <div />
      <FullScreenLoading opacity={1} />
      {/* this div handles last-of-type styling that normally offsets the nav */}
      <div />
    </>
  );

  if (showSunsetModal) {
    return <SunsetModal />;
  }

  return (
    <>
      {loggedIn && <Header />}
      {loggedIn && isMobile && (
        <>
          <SystemNotification isMobile margin={2} />
        </>
      )}
      {isLoggingOut && <CustomFullScreenLoading />}
      <VersionDetect isLatestVersion={isLatestVersion} onRefresh={emptyCacheStorage} />
      <StyledApp loggedIn={loggedIn}>
        {loggedIn && <Navigation drawerOpen={sideDrawerOpen} />}
        <Suspense fallback={<CustomFullScreenLoading />}>
          <Routes
            allowCybersourcePaymentsConditions={allowCybersourcePaymentsConditions}
            billingAndMonthlyTypeConditions={billingAndMonthlyTypeConditions}
            currentOwnerConditions={currentOwnerConditions}
            isCurrentOwner={isCurrentOwner}
            isRelatedContact={isRelatedContact}
            location={location}
          />
        </Suspense>
      </StyledApp>
    </>
  );
};

AppAndRoutes.propTypes = {
  allowCybersourcePaymentsConditions: PropTypes.shape({}).isRequired,
  billingAndMonthlyTypeConditions: PropTypes.shape({}).isRequired,
  currentOwnerConditions: PropTypes.shape({}).isRequired,
  isCurrentOwner: PropTypes.bool,
  isRelatedContact: PropTypes.bool,
  isLoggingOut: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  sideDrawerOpen: PropTypes.bool.isRequired,
};
AppAndRoutes.defaultProps = {
  isCurrentOwner: true,
  isRelatedContact: false,
};

const RouterApp = ({ isMobile }) => {
  const sideDrawerOpen = useSideDrawerOpen();
  const loggedIn = useIsLoggedIn();
  const isLoggingOut = useIsLoggingOut();
  const billingEnabled = useBillingEnabled();
  const isCurrentOwner = useCurrentOwner();
  const isRelatedContact = useRelatedContact();
  const monthlyType = useMonthlyType();
  const isOrangePostPTO = useIsOrangePostPTO();
  const isBlueBillingSystem = useIsBlueBillingSystem();
  const isEVChargerProspect = useIsEVChargerProspect();

  const billingAndMonthlyTypeConditions = {
    purchasedThru: billingEnabled,
    monthlyType,
    isBlue: !isOrangePostPTO || isBlueBillingSystem,
    notEV: !isEVChargerProspect,
  };

  const allowCybersourcePaymentsConditions = {
    isAvailable: !isOrangePostPTO || isBlueBillingSystem,
  };

  const currentOwnerConditions = {
    currentOwner: isCurrentOwner,
  };

  return (
    <BrowserRouter>
      <AppAndRoutes
        allowCybersourcePaymentsConditions={allowCybersourcePaymentsConditions}
        billingAndMonthlyTypeConditions={billingAndMonthlyTypeConditions}
        currentOwnerConditions={currentOwnerConditions}
        isCurrentOwner={isCurrentOwner}
        isLoggingOut={isLoggingOut}
        isMobile={isMobile}
        isRelatedContact={isRelatedContact}
        loggedIn={loggedIn}
        sideDrawerOpen={sideDrawerOpen}
      />
    </BrowserRouter>
  );
};

RouterApp.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};

const RouterAppWithSizes = withSizes(mapSizesToProps)(RouterApp);

const AppContainer = props => {
  return (
    <>
      <GlobalStyles />
      <ErrorBoundary render={message => <FullScreenError message={message} />}>
        <Provider store={store(sharedData)}>
          <FlagsmithContainer>
            <CookieLoginContainer>
              <PerformanceProvider>
                <MaterialUITheme>
                  <RouterAppWithSizes />
                </MaterialUITheme>
              </PerformanceProvider>
            </CookieLoginContainer>
          </FlagsmithContainer>
        </Provider>
      </ErrorBoundary>
    </>
  );
};

export default AppContainer;
